import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import './AINewsAggregatorPage.css';

interface NewsStory {
  title: string;
  url: string;
  discuss_url: string;
  points: number;
  comments: number;
  signal_score: number;
  source: 'hackernews' | 'reddit' | 'lobsters' | 'devto' | 'googlenews';
  sources?: string[];
  importance?: number;
  subreddit?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  hackernews: 'HN',
  lobsters: 'Lobsters',
  devto: 'DEV',
  googlenews: 'Google News',
  reddit: 'Reddit',
};

const sourceLabel = (source: string, story: NewsStory): string =>
  source === 'reddit'
    ? (story.subreddit ? `r/${story.subreddit}` : 'Reddit')
    : SOURCE_LABELS[source] || source;

interface Paper {
  title: string;
  authors: string[];
  summary: string;
  link: string;
  published: string;
}

interface AINewsData {
  generated_at: string;
  daily: {
    stories: NewsStory[];
    papers: Paper[];
  };
  weekly: {
    stories: NewsStory[];
    papers: Paper[];
  };
}

const AINewsAggregatorPage: React.FC = () => {
  const [news, setNews] = useState<AINewsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/ai-news.json')
      .then(res => res.json())
      .then(data => {
        setNews(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const renderStoryList = (stories: NewsStory[]) => (
    <ul className="news-list">
      {stories.map((story, index) => (
        <li key={index} className="news-item">
          <div className="news-item-main">
            <span className="news-rank">{index + 1}.</span>
            <a 
              href={story.url || story.discuss_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="news-title"
            >
              {story.title}
            </a>
          </div>
          <div className="news-meta">
            <span className="news-sources">
              {(story.sources && story.sources.length ? story.sources : [story.source]).map(src => (
                <span key={src} className={`news-source ${src}`}>
                  {sourceLabel(src, story)}
                </span>
              ))}
            </span>
            {story.points > 0 && <span className="news-points">{story.points} pts</span>}
            {story.comments > 0 && <span className="news-comments">{story.comments} comments</span>}
            <a
              href={story.discuss_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="news-discuss"
            >
              discuss
            </a>
          </div>
        </li>
      ))}
    </ul>
  );

  const renderPaperList = (papers: Paper[]) => (
    <ul className="paper-list">
      {papers.map((paper, index) => {
        const authorStr =
          paper.authors.slice(0, 3).join(', ') +
          (paper.authors.length >= 3 ? ' et al.' : '');
        return (
          <li key={index} className="paper-item">
            <a
              href={paper.link}
              target="_blank"
              rel="noopener noreferrer"
              className="paper-title"
            >
              {paper.title}
            </a>
            <div className="paper-meta">
              <span className="paper-authors">{authorStr}</span>
              {paper.published && (
                <span className="paper-date">{formatDate(paper.published)}</span>
              )}
            </div>
            {paper.summary && (
              <p className="paper-summary">
                {paper.summary.length > 280
                  ? `${paper.summary.slice(0, 280)}…`
                  : paper.summary}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <div className="ai-news-agg-page">
        <div className="ai-news-agg-container">
          <Link to="/" className="back-link">← Back to Home</Link>

          <header className="ai-news-agg-header">
            <h1>AI News Aggregator</h1>
            <p className="ai-news-agg-description">
              Automated daily digest of high-signal AI news from Hacker News, Lobsters, Dev.to,
              Google News, Reddit, and ArXiv. Stories are scored by relevance signals and filtered
              to remove opinion pieces and fluff.
            </p>
            {news && (
              <p className="last-updated">
                Last updated: {formatDate(news.generated_at)}
              </p>
            )}
          </header>

          {loading ? (
            <div className="news-loading-full">Loading news...</div>
          ) : news ? (
            <>
              <section className="news-section">
                <h2>Today's Top 10</h2>
                <p className="section-subtitle">High-signal AI stories from the past 24 hours</p>
                {news.daily.stories.length > 0 ? (
                  renderStoryList(news.daily.stories.slice(0, 10))
                ) : (
                  <p className="no-news">No daily stories available. Check back tomorrow.</p>
                )}
              </section>

              <section className="news-section">
                <h2>This Week's Top 10</h2>
                <p className="section-subtitle">Best AI stories from the past 7 days</p>
                {news.weekly.stories.length > 0 ? (
                  renderStoryList(news.weekly.stories.slice(0, 10))
                ) : (
                  <p className="no-news">No weekly stories available.</p>
                )}
              </section>

              <section className="news-section">
                <h2>Notable Papers</h2>
                <p className="section-subtitle">Recent ArXiv preprints from cs.AI, cs.CL, and cs.LG</p>
                {news.weekly.papers.length > 0 ? (
                  renderPaperList(news.weekly.papers)
                ) : (
                  <p className="no-news">No papers available.</p>
                )}
              </section>

              <section className="about-section">
                <h2>How It Works</h2>
                <div className="about-content">
                  <h3>Sources</h3>
                  <ul className="about-list">
                    <li><strong>Hacker News</strong> - via Algolia API, searching for AI/ML keywords</li>
                    <li><strong>Lobsters</strong> - the "ai" tag feed, a high-quality tech aggregator</li>
                    <li><strong>Dev.to</strong> - top "ai", "machinelearning", and "llm" tagged articles</li>
                    <li><strong>Google News</strong> - AI/LLM/ML headline search across major outlets</li>
                    <li><strong>Reddit</strong> - r/MachineLearning, r/LocalLLaMA, r/artificial, r/LanguageTechnology (when API access is available)</li>
                    <li><strong>ArXiv</strong> - cs.AI, cs.CL, cs.LG categories</li>
                  </ul>
                  
                  <h3>Ranking by Importance</h3>
                  <p>
                    Stories are deduplicated across sources by URL and title. When the same story
                    is covered by multiple sources, the entries are merged (and every source is
                    listed) and the story is boosted — cross-source corroboration is a strong signal
                    that something actually matters, independent of any single platform's vote count.
                  </p>

                  <h3>Signal Scoring</h3>
                  <p>Each story is scored to filter signal from noise:</p>
                  
                  <h4>Positive Signals (+points)</h4>
                  <ul className="about-list">
                    <li>Official domains (anthropic.com, openai.com, github.com, etc.)</li>
                    <li>Release keywords: "releases", "launches", "announces", "ships"</li>
                    <li>Version patterns: v2.0, beta, alpha, GA</li>
                    <li>Technical terms: API, SDK, benchmark, SOTA, open-source</li>
                    <li>Major company + action: "OpenAI releases", "Anthropic announces"</li>
                    <li>High engagement: 100+ comments, 300+ comments</li>
                  </ul>
                  
                  <h4>Negative Signals (-points)</h4>
                  <ul className="about-list">
                    <li>Personal opinion: "How I...", "My journey...", "What I learned..."</li>
                    <li>Question headlines: "Is AI...", "Will GPT-5...?"</li>
                    <li>Listicles: "10 tips...", "5 reasons..."</li>
                    <li>Hype words: "game-changer", "revolutionary", "mind-blown"</li>
                    <li>Fluff: "the future of", "what it means for", "hot take"</li>
                  </ul>
                  
                  <h4>Thresholds</h4>
                  <ul className="about-list">
                    <li>Hacker News: minimum 20 points</li>
                    <li>Lobsters: minimum 3 points</li>
                    <li>Dev.to: minimum 10 reactions</li>
                    <li>Reddit: minimum 50 upvotes</li>
                    <li>All stories: signal score &gt; 0 to be included</li>
                  </ul>
                </div>
              </section>
            </>
          ) : (
            <div className="news-loading-full">Failed to load news data.</div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AINewsAggregatorPage;
